import React, { useMemo, useCallback } from 'react'
import { modifierFormHelpers } from './modifier-form-helpers'
import { trackAddProduct, trackRemoveProduct } from '../../utils/ga-ecommerce'
import { toast } from 'react-toastify'
import { ToastNotification, toastEachWarning } from './ToastNotification/ToastNotification'
import { useFulfillment } from '../FulfillmentProvider/FulfillmentProvider'
import { useOutpost } from '../OutpostProvider/OutpostProvider'
import { dataByTypename } from '../../utils/apollo-helpers'

export const useHandleSubmit = ({
  itemDetails,
  restaurant,
  getComputedPrice,
  basePrice,
  cartGuid,
  itemGuid,
  itemGroupGuid,
  getModifierGroups,
  updateCart,
  selectionGuid
}) => {
  const { fulfillmentType, fulfillmentTime, diningOptionBehavior, deliveryInfo } = useFulfillment()
  const { isOutpost } = useOutpost()
  return useCallback(async (values, { setSubmitting }) => {
    const { specialInstructions, quantity } = values
    const price = getComputedPrice(values, quantity) + basePrice
    let createCartInput = {
      restaurantGuid: restaurant.guid,
      orderSource: 'ONLINE',
      cartFulfillmentInput: {
        fulfillmentDateTime: fulfillmentTime,
        fulfillmentType: fulfillmentType,
        diningOptionBehavior,
        deliveryInfo
      }
    }
    if (cartGuid) {
      createCartInput = undefined
    }
    const variables = {
      input: {
        cartGuid,
        createCartInput,
        selection: {
          itemGuid,
          itemGroupGuid,
          quantity,
          modifierGroups: getModifierGroups(values, quantity),
          specialInstructions
        },
        ...(selectionGuid && { selectionGuid })
      }
    }

    if (values.fractionalQuantity) {
      variables.input.selection.fractionalQuantity = {
        unitOfMeasure: itemDetails.unitOfMeasure,
        quantity: parseFloat(values.fractionalQuantity)
      }
    }

    if (isOutpost && !selectionGuid) {
      variables.diningOptionBehavior = 'OUTPOST'
    }

    try {
      const response = await updateCart({ variables })
      const responseData = response && response.data &&
        (response.data.addItemToCartV2 || response.data.editItemInCartV2)
      const { CartResponse, CartOutOfStockError } = dataByTypename(responseData)
      if (CartResponse) {
        trackAddProduct({ id: itemGuid, name: itemDetails.name, price, quantity })

        const text = `${quantity} item${quantity > 1 ? 's' : ''} added`
        toast(<ToastNotification checkmark>{text}</ToastNotification>)
      }
      if (CartOutOfStockError) {
        toastEachWarning({ warnings: [CartOutOfStockError.message] })
      }
    } finally {
      setSubmitting(false)
    }
  },
  [
    getComputedPrice,
    basePrice,
    restaurant,
    fulfillmentTime,
    fulfillmentType,
    diningOptionBehavior,
    deliveryInfo,
    cartGuid,
    itemGuid,
    itemGroupGuid,
    getModifierGroups,
    selectionGuid,
    isOutpost,
    itemDetails,
    updateCart
  ])
}

export const useHandleDelete = ({
  deleteItemFromCart,
  cartGuid,
  itemGuid,
  selectionGuid,
  getComputedPrice,
  basePrice,
  itemDetails
}) => useCallback(async values => {
  const response = await deleteItemFromCart({
    variables: {
      input: {
        cartGuid,
        selectionGuid
      }
    }
  })

  const responseData = response && response.data && response.data.deleteItemFromCartV2
  const { CartResponse, CartOutOfStockError } = dataByTypename(responseData)
  if (CartResponse) {
    const currentCartQuantity = itemDetails.quantity
    const price = getComputedPrice(values, currentCartQuantity) + basePrice
    trackRemoveProduct({
      id: itemGuid,
      name: itemDetails.name,
      price,
      quantity: currentCartQuantity
    })
  }
  if (CartOutOfStockError) {
    toastEachWarning({ warnings: [CartOutOfStockError.message] })
  }
}, [
  deleteItemFromCart,
  cartGuid,
  itemGuid,
  selectionGuid,
  getComputedPrice,
  basePrice,
  itemDetails
])

export const useModifierFormHelpers = ({
  itemDetails,
  itemGroupGuid,
  usesFractionalQuantity,
  fractionalQuantity
}) =>
  useMemo(() => {
    if (!itemDetails) return {}

    const {
      schema,
      initialValues,
      getModifierGroups,
      getComputedPrice
    } = modifierFormHelpers({
      modifierGroups: itemDetails.modifierGroups,
      quantity: itemDetails.quantity,
      specialInstructions: itemDetails.specialRequest || '',
      itemGroupGuid,
      usesFractionalQuantity,
      fractionalQuantity
    })

    return {
      getComputedPrice,
      formikProps: {
        validationSchema: schema,
        initialValues,
        isInitialValid: schema.isValidSync(initialValues)
      },
      getModifierGroups
    }
  }, [fractionalQuantity, itemDetails, itemGroupGuid, usesFractionalQuantity])
